import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferCapacityWithAgesSelect from 'components/OfferPage/Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import TourV2DepartureList from './Tourv2DepartureList'
import TourV2DepartureMonthsCarousel from './TourV2DepartureMonthsCarousel'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TourV2DepartureYearFilters from './TourV2DepartureYearFilters'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import TourV2DeparturesByYearList from './TourV2DeparturesByYearList'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferCalendarLuxPlusToggle from 'luxPlus/components/OfferCalendarLuxPlusToggle'
import { sum, uniqueBy } from 'lib/array/arrayUtils'
import Backdrop from 'components/utils/Backdrop'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import ModalContext from 'contexts/ModalContext'
import OfferCalendarCapacityChip from 'components/OfferPage/Filters/CalendarPicker/CalendarCapacity/OfferCalendarCapacityChip'
import Group from 'components/utils/Group'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { addToCheckoutCart, initialiseCheckout } from 'actions/CheckoutActions'
import generateTourV2CheckoutItem from 'lib/tours/generateTourV2CheckoutItems'
import { areDeparturesSoldOut, getDepartureCheapestOptionForTravellers } from 'lib/tours/tourDepartureUtils'
import TourV2DepartureBookingModal from './TourV2DepartureBookingModal'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addToCartGTM, addTourToCart } from 'analytics/eventDefinitions'
import { addToCart } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent, addGTMEvent } from 'api/googleTagManager'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import GeoContext from 'contexts/geoContext'
import { trackEvent } from 'analytics/analytics'
import { getCalendarDepartures, getFilteredDepartures, getIsPrivateRequestEnabled } from 'selectors/TourV2/tourV2OfferPageSelectors'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import TourDepositBanner from '../TourDepositBanner'
import TourPriceToggle from 'components/Tours/TourPriceToggle'
import { ALL_TOUR_PACKAGE_TYPE } from 'constants/tours'
import { getTourFlightsEnabled } from 'selectors/getTourFlightsEnabled'
import { hasValidTourAdultAgesForFlightClassification } from 'lib/offer/offerPageUtil'
import PrivateDepartureMessagingTile from '../AdditionalWaysToExperience/PrivateDepartureMessagingTile'
import { useWeddingGiftRegistryContext } from 'weddings/contexts/WeddingGiftRegistryContext'
import OfferPageLoyaltyPointsSlider from 'components/OfferPage/Loyalty/OfferPageLoyaltyPointsSlider'

const FilterRow = styled.div`
  display: grid;
  grid-template-columns: ${rem(280)} 1fr ${rem(280)};
  align-items: center;
  justify-items: center;
  gap: ${rem(12)};
`

const CapacityFilterWrapper = styled.div`
  grid-column: 1;
  justify-self: start;
`

const YearFilterWrapper = styled.div`
  grid-column: 2;
  justify-self: center;
`

const PriceToggleWrapper = styled.div`
  grid-column: 3;
  justify-self: end;
`

const CapacityFilter = styled(OfferCapacityWithAgesSelect)`
  width: 100%;
`

const LoyaltyPointsSliderWrapper = styled.div`
  margin-block: ${rem(20)};
`

interface Props {
  offer: App.Tours.TourV2Offer
  departureYear?: number
  monthCarouselRef?: React.Ref<HTMLDivElement>
  activeFilter?: OfferPageFilterType
  selectedDeparture?: App.Tours.TourV2OfferDeparture
  allDepartures: Array<App.Tours.TourV2OfferDeparture>
  calendarDepartures: Array<App.Tours.TourV2OfferDeparture>
  filteredDepartures: Array<App.Tours.TourV2OfferDeparture>
  keepBrowsing?: boolean
  variation: App.Tours.TourV2OfferVariation
  travellers?: Array<OfferPageRoom>
  packageType?: string
}

interface MappedStateProps {
  isPrivateRequestEnabled: boolean
  tourFlightsEnabled: boolean
}

function TourV2Departures(props: Props & MappedStateProps) {
  const {
    activeFilter,
    offer,
    departureYear,
    monthCarouselRef,
    selectedDeparture,
    allDepartures,
    calendarDepartures,
    filteredDepartures,
    keepBrowsing,
    variation,
    travellers,
    packageType,
    isPrivateRequestEnabled,
    tourFlightsEnabled,
  } = props

  const intl = useIntl()
  const filterRowRef = useRef<HTMLDivElement>(null)
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()
  const offerPageDispatch = useOfferPageDispatch()
  const { currentCurrency } = useContext(GeoContext)
  const { isActive: isWeddingGiftRegistry, addItems: addItemsToWeddingGiftRegistryCart } = useWeddingGiftRegistryContext()

  const hasMultipleYears = useMemo(() => {
    const uniqueYears = uniqueBy(allDepartures, dep => new Date(dep.startDate).getFullYear())
    return uniqueYears.length > 1
  }, [allDepartures])

  useEffect(() => {
    if (activeFilter === 'capacity') {
      filterRowRef.current?.scrollIntoView({ block: 'start' })
    } else if (selectedDeparture) {
      offerPageDispatch({ type: OfferPageActions.setTourV2Departure, departure: undefined })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter])

  const setDepartureYear = useCallback((year: number) => offerPageDispatch({ type: OfferPageActions.setDepartureYear, year }), [offerPageDispatch])

  const onMobileCapacityClick = useCallback(() => {
    showModal(<TourV2DepartureBookingModal
      offer={offer}
      defaultStep="travellers"
      packageType={packageType}
    />)
  }, [offer, showModal, packageType])

  const onTravellersApply = useCallback((travellers: Array<App.Occupants>) => {
    if (!hasValidTourAdultAgesForFlightClassification(travellers, offer, tourFlightsEnabled, variation)) return
    offerPageDispatch({ type: OfferPageActions.setRooms, rooms: travellers as any })
    if (selectedDeparture) {
      const items = travellers.map(traveller => {
        const resolvedPackageType = packageType && packageType !== ALL_TOUR_PACKAGE_TYPE ? packageType : undefined
        const option = getDepartureCheapestOptionForTravellers(selectedDeparture, traveller, resolvedPackageType)
          ?? getDepartureCheapestOptionForTravellers(selectedDeparture, traveller)
        if (option) {
          return generateTourV2CheckoutItem(
            offer,
            selectedDeparture,
            traveller,
            option,
          )
        }
      })

      if (items.every(item => !!item)) {
        if (isWeddingGiftRegistry) {
          addItemsToWeddingGiftRegistryCart(items)
          offerPageDispatch({ type: OfferPageActions.setKeepBrowsing, keepBrowsing: false })
        } else if (keepBrowsing) {
          dispatch(addToCheckoutCart(items))
          offerPageDispatch({ type: OfferPageActions.setKeepBrowsing, keepBrowsing: false })
          showSnackbar(intl.formatMessage({
            id: 'IgvhfR9eN6yB2ydd6uAw',
            defaultMessage: 'Your item has been added to the cart.',
            description: 'Snackbar message when item is added to cart in the cart view',
          }), 'success', {
            action: {
              label: intl.formatMessage({
                id: 'XGuesJwGws5Vn-ObJ-fs',
                defaultMessage: 'View cart',
                description: 'Snackbar action label to view cart in the shopping cart interface',
              }),
              to: '/cart',
            },
          })
        } else {
          dispatch(initialiseCheckout(items))
          dispatch(navigateToCheckout())
        }

        const totalTravellers = sumUpOccupancies(travellers)
        const priceTotal = sum(items, item => item.total ?? 0)
        fireEcommerceGTMEvent(addToCartGTM(offer, currentCurrency, priceTotal, totalTravellers, 'tour'))
        addGTMEvent(addTourToCart(offer, currentCurrency, priceTotal, totalTravellers))
        trackEvent(addToCart())
      }
    }
  }, [offerPageDispatch, selectedDeparture, dispatch, offer, currentCurrency, keepBrowsing, packageType, intl, isWeddingGiftRegistry, addItemsToWeddingGiftRegistryCart, variation, tourFlightsEnabled])

  const soldOutReason = useMemo(() => {
    return areDeparturesSoldOut(calendarDepartures, travellers)
  }, [calendarDepartures, travellers])

  const dropdownCtaText = useMemo(() => {
    return selectedDeparture
      ? intl.formatMessage({
          id: 'Mj6PHpr9dNRoX7gjPA-9',
          defaultMessage: 'Continue',
          description: 'Dropdown CTA text when departure is selected in the tours section',
        })
      : intl.formatMessage({
          id: '_k5ObrUKN0Z2GPUcDDIO',
          defaultMessage: 'Apply',
          description: 'Dropdown CTA text when no departure is selected in the tours section',
        })
  }, [selectedDeparture, intl])

  return <div>
    <CSSBreakpoint min="tablet">
      <LayoutContainer>
        <VerticalSpacer gap={32}>
          <FilterRow ref={filterRowRef}>
            <Backdrop visible={!!activeFilter} />
            <CapacityFilterWrapper>
              <CapacityFilter
                capacities={offer.roomCapacity}
                occupantUnit="traveller"
                maxRooms={5}
                boundaryRef={filterRowRef}
                onApply={onTravellersApply}
                dropdownCtaText={dropdownCtaText}
              />
            </CapacityFilterWrapper>
            {
              allDepartures.length > 1 && hasMultipleYears &&
              <YearFilterWrapper>
                <TourV2DepartureYearFilters
                  departures={allDepartures}
                  departureYear={departureYear}
                  onSelect={setDepartureYear}
                />
              </YearFilterWrapper>
            }
            <PriceToggleWrapper>
              <TourPriceToggle />
            </PriceToggleWrapper>
          </FilterRow>
          {
            allDepartures.length > 1 &&
            <TourV2DepartureMonthsCarousel
              departures={allDepartures}
              ref={monthCarouselRef}
            />
          }
        </VerticalSpacer>
      </LayoutContainer>
      <LoyaltyPointsSliderWrapper>
        <OfferPageLoyaltyPointsSlider InnerWrapper={LayoutContainer} />
      </LoyaltyPointsSliderWrapper>
      <LayoutContainer>
        <VerticalSpacer gap={32}>
          {isPrivateRequestEnabled && <PrivateDepartureMessagingTile
            offer={offer}
            variationId={variation?.id}
            defaultTravellers={travellers}
          />}
          <TourDepositBanner offer={offer} />
          <TourV2DepartureList
            offer={offer}
            departures={filteredDepartures}
            packageType={packageType}
          />
        </VerticalSpacer>
      </LayoutContainer>
    </CSSBreakpoint>
    <CSSBreakpoint max="mobile">
      <VerticalSpacer gap={28}>
        <LayoutContainer>
          <VerticalSpacer gap={12}>
            {hasMultipleYears && <HorizontalContainerWithFadedOverflow>
              <TourV2DepartureYearFilters
                departures={allDepartures}
                departureYear={departureYear}
                onSelect={setDepartureYear}
              />
            </HorizontalContainerWithFadedOverflow>}
            <Group direction="horizontal" gap={8} wrap="wrap">
              {offer.luxPlus.hasMemberPrices && <OfferCalendarLuxPlusToggle />}
              <OfferCalendarCapacityChip
                onClick={onMobileCapacityClick}
                offer={offer}
              />
              {variation.departures.length <= 4 && !soldOutReason && <TourPriceToggle />}
            </Group>
          </VerticalSpacer>
        </LayoutContainer>
        <LoyaltyPointsSliderWrapper>
          <OfferPageLoyaltyPointsSlider InnerWrapper={LayoutContainer} />
        </LoyaltyPointsSliderWrapper>
        <LayoutContainer>
          <VerticalSpacer gap={28}>
            {(isPrivateRequestEnabled) && <PrivateDepartureMessagingTile
              offer={offer}
              variationId={variation?.id}
              defaultTravellers={travellers}
            />}
            <TourDepositBanner offer={offer} />
          </VerticalSpacer>
        </LayoutContainer>
        <TourV2DeparturesByYearList
          offer={offer}
          departures={calendarDepartures}
          packageType={packageType}
        />
      </VerticalSpacer>
    </CSSBreakpoint>
  </div>
}

type MappedProps = Pick<Props,
  'offer' |
  'departureYear' |
  'activeFilter' |
  'selectedDeparture' |
  'allDepartures' |
  'calendarDepartures' |
  'filteredDepartures' |
  'keepBrowsing' |
  'travellers'
>

export default withOfferPageAndReduxState((state, appState): MappedProps & MappedStateProps => {
  return {
    offer: state.tourV2Offer!,
    allDepartures: state.tourV2Variation!.departures,
    departureYear: state.departureYear,
    activeFilter: state.activeFilter,
    selectedDeparture: state.tourV2Departure,
    calendarDepartures: getCalendarDepartures(state),
    filteredDepartures: getFilteredDepartures(state),
    keepBrowsing: state.keepBrowsing,
    travellers: state.rooms,
    isPrivateRequestEnabled: getIsPrivateRequestEnabled(state, appState),
    tourFlightsEnabled: getTourFlightsEnabled(appState),
  }
}, TourV2Departures)
